import React from 'react';
import { Box, Typography, Button, Grid, Container, Chip } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import heroImg from '../../../images/edu_crm_hero.png';

const badges = ['Lead Capture', 'Auto Follow-ups', 'Admission Pipeline', 'Reports'];

const Hero = () => (
  <Box sx={{
    background: 'linear-gradient(135deg, #0f1535 0%, #1a2580 40%, #4a2d9e 75%, #684df4 100%)',
    minHeight: '92vh',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    pt: { xs: 10, md: 0 },
    pb: { xs: 8, md: 0 },
  }}>
    {/* Decorative Circles */}
    <Box sx={{ position:'absolute', width:600, height:600, borderRadius:'50%', background:'radial-gradient(circle, rgba(104,77,244,0.18) 0%, transparent 70%)', top:-150, right:-150, pointerEvents:'none' }} />
    <Box sx={{ position:'absolute', width:400, height:400, borderRadius:'50%', background:'radial-gradient(circle, rgba(35,48,152,0.25) 0%, transparent 70%)', bottom:-100, left:-100, pointerEvents:'none' }} />
    {/* Dot Grid */}
    <Box sx={{ position:'absolute', inset:0, backgroundImage:'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize:'32px 32px', pointerEvents:'none' }} />

    <Container maxWidth="lg">
      <Grid container spacing={6} alignItems="center">
        {/* LEFT */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display:'inline-flex', alignItems:'center', gap:1, bgcolor:'rgba(104,77,244,0.15)', border:'1px solid rgba(104,77,244,0.4)', borderRadius:'30px', px:2, py:0.6, mb:3 }}>
            <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:'#a78bfa', boxShadow:'0 0 8px #a78bfa' }} />
            <Typography sx={{ color:'#c4b5fd', fontSize:12, fontWeight:700, letterSpacing:1.5, fontFamily:'inherit', textTransform:'uppercase' }}>Education CRM Software</Typography>
          </Box>

          <Typography variant="h1" sx={{ color:'#fff', fontWeight:900, fontSize:{ xs:'2.2rem', md:'3.2rem' }, lineHeight:1.15, mb:3, fontFamily:"'Montserrat', sans-serif" }}>
            Turn Every Enquiry into{' '}
            <Box component="span" sx={{ background:'linear-gradient(90deg, #c4b5fd, #818cf8)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
              an Admission
            </Box>
          </Typography>

          <Typography sx={{ color:'rgba(255,255,255,0.72)', fontSize:17, mb:4, lineHeight:1.9, fontFamily:"'Montserrat', sans-serif", maxWidth:480 }}>
            SN Info Systems' Education CRM is built for counselors, admission teams, and institute managers — track every student from enquiry to enrollment without lifting a finger.
          </Typography>

          {/* Feature pills */}
          <Box sx={{ display:'flex', flexWrap:'wrap', gap:1.5, mb:4 }}>
            {badges.map(b => (
              <Chip key={b} icon={<CheckCircleOutlineIcon sx={{ color:'#a78bfa !important', fontSize:16 }} />} label={b}
                sx={{ bgcolor:'rgba(104,77,244,0.12)', color:'rgba(255,255,255,0.85)', border:'1px solid rgba(104,77,244,0.3)', fontFamily:"'Montserrat', sans-serif", fontSize:13 }} />
            ))}
          </Box>

          <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
            <Button variant="contained" endIcon={<ArrowForwardIcon />} size="large"
              sx={{ bgcolor:'#684df4', px:4, py:1.6, borderRadius:'12px', fontFamily:"'Montserrat', sans-serif", fontWeight:700, fontSize:15, boxShadow:'0 8px 32px rgba(104,77,244,0.4)', '&:hover':{ bgcolor:'#5538d4', transform:'translateY(-2px)', boxShadow:'0 12px 40px rgba(104,77,244,0.5)' }, transition:'all 0.2s' }}>
              Book Free Demo
            </Button>
            <Button variant="outlined" size="large"
              sx={{ borderColor:'rgba(255,255,255,0.3)', color:'#fff', px:4, py:1.6, borderRadius:'12px', fontFamily:"'Montserrat', sans-serif", '&:hover':{ borderColor:'rgba(255,255,255,0.7)', bgcolor:'rgba(255,255,255,0.05)' } }}>
              Download Brochure
            </Button>
          </Box>

          <Box sx={{ display:'flex', gap:4, mt:5 }}>
            {[['500+','Institutions'],['3x','More Admissions'],['98%','Retention Rate']].map(([v,l]) => (
              <Box key={l}>
                <Typography sx={{ color:'#c4b5fd', fontWeight:800, fontSize:'1.6rem', fontFamily:"'Montserrat', sans-serif", lineHeight:1 }}>{v}</Typography>
                <Typography sx={{ color:'rgba(255,255,255,0.55)', fontSize:13, fontFamily:"'Montserrat', sans-serif", mt:0.3 }}>{l}</Typography>
              </Box>
            ))}
          </Box>
        </Grid>

        {/* RIGHT — Dashboard Mockup */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position:'relative' }}>
            {/* Glow */}
            <Box sx={{ position:'absolute', inset:-40, background:'radial-gradient(ellipse, rgba(104,77,244,0.3) 0%, transparent 65%)', pointerEvents:'none' }} />
            <Box sx={{
              borderRadius:'20px', overflow:'hidden',
              boxShadow:'0 40px 100px rgba(0,0,0,0.5), 0 0 0 1px rgba(104,77,244,0.3)',
              transform:'perspective(1200px) rotateY(-8deg) rotateX(2deg)',
              transition:'transform 0.4s ease',
              '&:hover':{ transform:'perspective(1200px) rotateY(0deg) rotateX(0deg)' },
            }}>
              <img src={heroImg} alt="Education CRM Dashboard" style={{ width:'100%', display:'block' }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default Hero;
